// Signing. Outside signers arrive at /s/{token} with no account: open → email code → session → consent → sign.
// Members of the sending company may verify the same link by signing in to eSignIt instead (code stays as a
// fallback). The sender signs their own part at /envelopes/{id}/sign with a session issued after sign-in.
// The header shows the sending company's logo and colour (design §6); eSignIt stays in the footer.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowDown, Ban, CheckCircle2, Clock, Download, Lock, MailCheck, PenLine, ShieldCheck } from "lucide-react";
import { api, ApiError, signerApi, startDownload } from "../lib/api";
import { idToken, loadConfig } from "../lib/auth";
import { FIELD_LABEL, rColor } from "../lib/format";
import type { Field, PageInfo } from "../lib/types";
import { CompanyMark, Logo } from "../components/icons";
import { useMe } from "../App";
import { PdfPages } from "../components/PdfPages";
import { SignaturePad } from "../components/SignaturePad";
import { Button, ErrorNote, Modal, Spinner, cx } from "../components/ui";

interface Opened {
  state: string;
  needsCode: boolean;
  /** A member of the sending company: may sign in instead of using a code. */
  member?: boolean;
  brand?: Brand;
  title: string;
  senderName: string;
  senderEmail: string;
  recipientName: string;
  emailMasked: string;
  voidReason?: string;
}

interface Brand {
  company: string;
  color: string;
  logo?: string;
}

const sessionKey = (token: string) => `esignit-session:${token.slice(0, 16)}`;
const readSession = (token: string) => {
  try {
    return sessionStorage.getItem(sessionKey(token));
  } catch {
    return null;
  }
};
const writeSession = (token: string, s: string | null) => {
  try {
    if (s) sessionStorage.setItem(sessionKey(token), s);
    else sessionStorage.removeItem(sessionKey(token));
  } catch {
    /* private mode: the session just won't survive a reload */
  }
};

export function SignerApp({ token }: { token: string }) {
  const [opened, setOpened] = useState<Opened | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(() => readSession(token));

  useEffect(() => {
    signerApi<Opened>("/open", { body: { token } }).then(setOpened, (e) => setError((e as Error).message));
  }, [token]);

  const lost = () => {
    writeSession(token, null);
    setSession(null);
  };

  return (
    <SignerFrame brand={opened?.brand} title={opened?.title} from={opened ? `${opened.senderName} (${opened.senderEmail})` : undefined}>
      {error ? (
        <Notice icon={<Ban className="size-8 text-bad" />} title="This link doesn't work">
          {error}. If you were expecting to sign something, ask the sender to send it again.
        </Notice>
      ) : !opened ? (
        <Spinner />
      ) : session && opened.needsCode ? (
        <SigningRoom session={session} onSessionLost={lost} />
      ) : opened.needsCode && opened.member ? (
        <MemberStep token={token} opened={opened} onSession={(s) => (writeSession(token, s), setSession(s))} />
      ) : opened.needsCode ? (
        <CodeStep token={token} opened={opened} onSession={(s) => (writeSession(token, s), setSession(s))} />
      ) : (
        <StateNotice opened={opened} />
      )}
    </SignerFrame>
  );
}

export function SelfSign() {
  const me = useMe();
  const { id } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api<{ session: string }>(`/envelopes/${id}/self-session`, { body: {} }).then((r) => setSession(r.session), (e) => setError((e as Error).message));
  }, [id]);
  return (
    <SignerFrame back={`/envelopes/${id}`} brand={me.company ? { company: me.company.branding.displayName || me.company.name, color: me.company.branding.color, logo: me.company.branding.logoPng } : undefined}>
      {error ? <ErrorNote>{error}</ErrorNote> : !session ? <Spinner /> : <SigningRoom session={session} onSessionLost={() => nav(`/envelopes/${id}`)} onDone={() => nav(`/envelopes/${id}`)} />}
    </SignerFrame>
  );
}

function SignerFrame({ title, from, back, brand, children }: { title?: string; from?: string; back?: string; brand?: Brand; children: React.ReactNode }) {
  const color = brand && /^#[0-9a-f]{6}$/i.test(brand.color) ? brand.color : undefined;
  return (
    <div className="min-h-full flex flex-col" style={color ? ({ "--company": color } as React.CSSProperties) : undefined}>
      <div className={cx("h-1 shrink-0", !color && "brand-bar")} style={color ? { background: color } : undefined} aria-hidden />
      <header className="bg-surface border-b border-line px-4 h-14 flex items-center gap-3 shrink-0">
        {brand ? (
          <span className="shrink-0 max-w-40 flex items-center">
            <CompanyMark name={brand.company} logo={brand.logo} height={28} />
          </span>
        ) : (
          <Logo className="size-7 shrink-0" />
        )}
        <span className="h-6 w-px bg-line shrink-0" aria-hidden />
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{title ?? "eSignIt"}</div>
          {from && <div className="text-xs text-muted truncate">From {from}</div>}
        </div>
        {back && (
          <Link to={back} className="ml-auto text-sm text-muted hover:text-ink">
            Close
          </Link>
        )}
      </header>
      <main className="flex-1 min-h-0">{children}</main>
      <footer className="shrink-0 border-t border-line bg-surface px-4 py-3 text-xs text-muted flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
        <Lock className="size-3.5" aria-hidden /> {brand ? `Sent by ${brand.company} · ` : ""}Secure electronic signing by
        <a href="https://esignit.app" target="_blank" rel="noopener" className="inline-flex items-center gap-1 font-medium text-ink-2 hover:text-ink hover:underline underline-offset-2">
          <Logo className="size-3.5" /> eSignIt
        </a>
        ·
        <a href="/help#signing" target="_blank" rel="noopener" className="hover:text-ink hover:underline underline-offset-2">
          Help
        </a>
      </footer>
    </div>
  );
}

function Notice({ icon, title, children }: { icon: React.ReactNode; title: string; children?: React.ReactNode }) {
  return (
    <div className="max-w-md mx-auto mt-12 mx-4 sm:mx-auto bg-surface border border-line rounded-2xl p-8 text-center">
      <div className="flex justify-center">{icon}</div>
      <h1 className="text-lg font-semibold mt-3">{title}</h1>
      {children && <div className="text-sm text-muted mt-2">{children}</div>}
    </div>
  );
}

function StateNotice({ opened }: { opened: Opened }) {
  const m: Record<string, [React.ReactNode, string, string]> = {
    waiting: [<Clock className="size-8 text-accent" />, "Not your turn yet", `Someone else signs first. We'll email you when it's your turn.`],
    signed: [<CheckCircle2 className="size-8 text-good" />, "You've signed", "We'll email you the completed document once everyone has signed."],
    completing: [<ShieldCheck className="size-8 text-good" />, "Everyone has signed", "The completed document is being sealed and emailed to you now."],
    cc: [<MailCheck className="size-8 text-accent" />, "Nothing to sign", "You'll get a copy by email once everyone has signed."],
    declined: [<Ban className="size-8 text-bad" />, "Signing stopped", "A signer declined, so this document won't be completed."],
    voided: [<Ban className="size-8 text-warn" />, "This envelope was cancelled", opened.voidReason ? `Reason: “${opened.voidReason}”` : "The sender cancelled it."],
    expired: [<Clock className="size-8 text-warn" />, "This link has expired", "Ask the sender to send the document again if you still need to sign it."],
    gone: [<Clock className="size-8 text-muted" />, "This link has expired", "The completed copy was emailed to you when everyone signed."],
  };
  const [icon, title, text] = m[opened.state] ?? m.gone;
  return (
    <Notice icon={icon} title={title}>
      {text}
    </Notice>
  );
}

/** Where a sign-in that started on a signing link left an error (set by the sender app on return). */
export const ACCOUNT_ERROR = "esignit-account-error";

/**
 * Members of the sending company: sign in to eSignIt (their usual sign-in, SSO or password) and the server checks the
 * account is the one the link was sent to. Back from sign-in, the session is fetched straight away.
 */
function MemberStep({ token, opened, onSession }: { token: string; opened: Opened; onSession: (s: string) => void }) {
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useCode, setUseCode] = useState(false);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        await loadConfig();
        const back = sessionStorage.getItem(ACCOUNT_ERROR);
        if (back) {
          sessionStorage.removeItem(ACCOUNT_ERROR);
          setError(back);
        } else if (await idToken()) {
          const r = await api<{ session: string }>("/signer-session", { body: { token } });
          return onSession(r.session);
        }
      } catch (e) {
        setError((e as Error).message);
      }
      setBusy(false);
    })();
  }, [token, onSession]);
  if (useCode) return <CodeStep token={token} opened={opened} onSession={onSession} />;
  const completed = opened.state === "completed";
  const company = opened.brand?.company ?? "your company";
  return (
    <div className="max-w-md mx-auto mt-10 mx-4 sm:mx-auto bg-surface border border-line rounded-2xl p-7">
      <Lock className="size-7 text-accent" />
      <h1 className="text-lg font-semibold mt-3">{completed ? "Download your signed copy" : `Hi ${opened.recipientName}`}</h1>
      <p className="text-sm text-ink-2 mt-2">
        {completed ? <>Everyone has signed “{opened.title}”. </> : <>{opened.senderName} sent you “{opened.title}” to review and sign. </>}
        To make sure it's you, sign in to eSignIt with your {company} account (<span className="font-medium whitespace-nowrap">{opened.emailMasked}</span>).
      </p>
      <Button variant="primary" className="w-full h-11 mt-5" busy={busy} onClick={() => (setBusy(true), location.assign(`/?${new URLSearchParams({ back: `/s/${token}` })}`))}>
        Sign in to eSignIt
      </Button>
      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
      <p className="text-xs text-muted mt-4">
        Can't sign in?{" "}
        <button type="button" className="underline" onClick={() => setUseCode(true)}>
          Email me a code instead
        </button>
        .
      </p>
    </div>
  );
}

function CodeStep({ token, opened, onSession }: { token: string; opened: Opened; onSession: (s: string) => void }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await signerApi("/code", { body: { token } });
      setSent(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await signerApi<{ session: string }>("/verify", { body: { token, code } });
      onSession(r.session);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  const completed = opened.state === "completed";
  return (
    <div className="max-w-md mx-auto mt-10 mx-4 sm:mx-auto bg-surface border border-line rounded-2xl p-7">
      <Lock className="size-7 text-accent" />
      <h1 className="text-lg font-semibold mt-3">{completed ? "Download your signed copy" : `Hi ${opened.recipientName}`}</h1>
      <p className="text-sm text-ink-2 mt-2">
        {completed ? (
          <>Everyone has signed “{opened.title}”. </>
        ) : (
          <>
            {opened.senderName} sent you “{opened.title}” to review and sign.{" "}
          </>
        )}
        To make sure it's you, we'll email a 6-digit code to <span className="font-medium whitespace-nowrap">{opened.emailMasked}</span>.
      </p>
      {!sent ? (
        <Button variant="primary" className="w-full h-11 mt-5" busy={busy} onClick={send}>
          Email me a code
        </Button>
      ) : (
        <form
          className="mt-5"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <label htmlFor="code" className="block text-sm font-medium text-ink-2 mb-1">
            Code from the email
          </label>
          <input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            className="w-full h-12 rounded-lg border border-line bg-surface px-3 text-2xl tracking-[0.4em] text-center font-mono tabular"
            aria-describedby="code-help"
          />
          <Button type="submit" variant="primary" className="w-full h-11 mt-3" busy={busy} disabled={code.length !== 6}>
            Continue
          </Button>
          <p id="code-help" className="text-xs text-muted mt-3">
            The code expires in 10 minutes. Nothing arrived? Check spam, or{" "}
            <button type="button" className="underline" onClick={send} disabled={busy}>
              send another
            </button>
            .
          </p>
        </form>
      )}
      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
    </div>
  );
}

interface SignerView {
  state: string;
  title: string;
  message?: string;
  senderName: string;
  senderEmail: string;
  me: { rid: string; name: string; email: string; consentedAt?: string; status: string; color: number; isSender: boolean };
  disclosure: string[];
  needsConsent?: boolean;
  finalAvailable?: boolean;
  docUrl?: string;
  pages?: PageInfo[];
  fields?: Field[];
  others?: (Field & { imageUrl?: string })[];
  /** Members who signed in: may save, and get their saved images offered. */
  canSave?: boolean;
  saved?: { signature?: string; initials?: string };
}

function SigningRoom({ session, onSessionLost, onDone }: { session: string; onSessionLost: () => void; onDone?: () => void }) {
  const [view, setView] = useState<SignerView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const call = async <T,>(path: string, opts: { method?: string; body?: unknown } = {}) => {
    try {
      return await signerApi<T>(path, { ...opts, session });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onSessionLost();
      throw e;
    }
  };
  const load = () => call<SignerView>("/envelope").then(setView, (e) => setError((e as Error).message));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  if (error) return <div className="max-w-md mx-auto mt-10 px-4"><ErrorNote>{error}</ErrorNote></div>;
  if (!view) return <Spinner />;
  if (finished)
    return (
      <Notice icon={<CheckCircle2 className="size-10 text-good" />} title="Thanks, you've signed">
        {view.me.isSender ? "Your signature is recorded." : "You'll get an email with the completed document once everyone has signed. You can close this page."}
        {onDone && (
          <div className="mt-5">
            <Button variant="primary" onClick={onDone}>
              Back to envelope
            </Button>
          </div>
        )}
      </Notice>
    );
  if (view.finalAvailable) return <FinalDownload call={call} title={view.title} />;
  if (view.state !== "verify") return <StateNotice opened={{ state: view.state, needsCode: false, title: view.title, senderName: view.senderName, senderEmail: view.senderEmail, recipientName: view.me.name, emailMasked: "" }} />;
  if (view.needsConsent) return <Consent view={view} call={call} onAgreed={load} />;
  return <Document view={view} call={call} onFinished={() => setFinished(true)} />;
}

type Call = <T>(path: string, opts?: { method?: string; body?: unknown }) => Promise<T>;

function FinalDownload({ call, title }: { call: Call; title: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Notice icon={<ShieldCheck className="size-10 text-good" />} title="Everyone has signed">
      “{title}” is complete. Download the sealed PDF, which includes its Certificate of Completion.
      <div className="mt-5">
        <Button
          variant="primary"
          busy={busy}
          onClick={async () => {
            setBusy(true);
            try {
              startDownload((await call<{ url: string }>("/download")).url);
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Download className="size-4" /> Download signed PDF
        </Button>
      </div>
      {error && <p className="text-bad mt-2">{error}</p>}
    </Notice>
  );
}

function Consent({ view, call, onAgreed }: { view: SignerView; call: Call; onAgreed: () => void }) {
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="max-w-xl mx-auto mt-8 mb-10 px-4">
      <div className="bg-surface border border-line rounded-2xl p-6 sm:p-7">
        <h1 className="text-lg font-semibold">Before you sign</h1>
        {view.message && (
          <div className="mt-4 rounded-lg bg-accent-soft border-l-4 border-accent px-4 py-3">
            <div className="text-xs text-muted mb-1">Message from {view.senderName}</div>
            <div className="text-sm whitespace-pre-wrap">{view.message}</div>
          </div>
        )}
        <h2 className="text-sm font-semibold mt-5">Electronic record and signature disclosure</h2>
        <div className="mt-2 text-sm text-ink-2 space-y-2 max-h-64 overflow-y-auto pr-1">
          {view.disclosure.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
        <label className="flex items-start gap-2 mt-5 text-sm font-medium">
          <input type="checkbox" className="mt-0.5 size-4" checked={agree} onChange={(e) => setAgree(e.target.checked)} />I agree to use electronic records and signatures.
        </label>
        {error && (
          <div className="mt-3">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}
        <div className="flex flex-col-reverse sm:flex-row gap-2 mt-6">
          <Button variant="ghost" onClick={() => setDeclining(true)}>
            Decline to sign
          </Button>
          <Button
            variant="primary"
            className="sm:ml-auto h-10"
            disabled={!agree}
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await call("/consent", { body: {} });
                onAgreed();
              } catch (e) {
                setError((e as Error).message);
                setBusy(false);
              }
            }}
          >
            Continue to document
          </Button>
        </div>
      </div>
      {declining && <DeclineDialog call={call} onClose={() => setDeclining(false)} />}
    </div>
  );
}

function DeclineDialog({ call, onClose }: { call: Call; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (done)
    return (
      <Modal title="You declined" onClose={() => location.reload()}>
        <p className="text-sm text-ink-2 pb-4">The sender has been told. Nobody else can sign this envelope now. You can close this page.</p>
      </Modal>
    );
  return (
    <Modal
      title="Decline to sign?"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={!reason.trim()}
            onClick={async () => {
              setBusy(true);
              try {
                await call("/decline", { body: { reason } });
                setDone(true);
              } catch (e) {
                setError((e as Error).message);
                setBusy(false);
              }
            }}
          >
            Decline
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-2 mb-3">This stops the whole envelope: nobody else can sign it. The sender sees your reason.</p>
      <label className="block text-sm font-medium text-ink-2 mb-1" htmlFor="decline-reason">
        Reason
      </label>
      <textarea id="decline-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={3} className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm" />
      {error && <p className="text-sm text-bad mt-2">{error}</p>}
    </Modal>
  );
}

function Document({ view, call, onFinished }: { view: SignerView; call: Call; onFinished: () => void }) {
  const fields = useMemo(() => [...(view.fields ?? [])].sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x), [view.fields]);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [applied, setApplied] = useState<Set<string>>(new Set());
  const [sig, setSig] = useState<string | null>(null);
  const [ini, setIni] = useState<string | null>(null);
  const [pad, setPad] = useState<{ kind: "signature" | "initials"; fid: string } | null>(null);
  const [saveForNext, setSaveForNext] = useState<{ signature?: boolean; initials?: boolean }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const color = rColor(view.me.color);
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });

  const done = (f: Field) => {
    if (f.type === "signature" || f.type === "initials") return applied.has(f.fid);
    if (f.type === "text") return Boolean(String(values[f.fid] ?? "").trim());
    if (f.type === "checkbox") return values[f.fid] === true;
    return true;
  };
  const required = fields.filter((f) => f.required || f.type === "signature" || f.type === "initials");
  const remaining = required.filter((f) => !done(f));
  const nextField = () => {
    const f = remaining[0];
    if (!f) return;
    document.querySelector(`[data-fid="${f.fid}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    (document.querySelector(`[data-fid="${f.fid}"] input, [data-fid="${f.fid}"] button`) as HTMLElement | null)?.focus({ preventScroll: true });
  };
  const apply = (f: Field) => {
    const have = f.type === "signature" ? sig : ini;
    if (!have) return setPad({ kind: f.type as "signature" | "initials", fid: f.fid });
    setApplied((s) => {
      const n = new Set(s);
      if (n.has(f.fid)) n.delete(f.fid);
      else n.add(f.fid);
      return n;
    });
  };
  const finish = async () => {
    if (remaining.length) return nextField();
    setBusy(true);
    setError(null);
    try {
      const vals: Record<string, string | boolean> = {};
      for (const f of fields) if (f.type === "text" || f.type === "checkbox") vals[f.fid] = values[f.fid] ?? (f.type === "checkbox" ? false : "");
      await call("/finish", {
        body: {
          values: vals,
          signature: fields.some((f) => f.type === "signature") ? sig : undefined,
          initials: fields.some((f) => f.type === "initials") ? ini : undefined,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          saveForNext: { signature: Boolean(saveForNext.signature && sig), initials: Boolean(saveForNext.initials && ini) },
        },
      });
      onFinished();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col min-h-full">
      <div className="sticky top-0 z-20 bg-surface/95 backdrop-blur border-b border-line px-4 py-2.5 flex items-center gap-3">
        <div className="text-sm min-w-0">
          {remaining.length ? (
            <span>
              <span className="font-semibold tabular">{remaining.length}</span> required {remaining.length === 1 ? "field" : "fields"} left
            </span>
          ) : (
            <span className="text-good font-medium">All done. Select Finish to sign.</span>
          )}
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={() => setDeclining(true)} className="max-sm:hidden">
            Decline
          </Button>
          {remaining.length > 0 && (
            <Button onClick={nextField}>
              <ArrowDown className="size-4" /> Next
            </Button>
          )}
          <Button variant="primary" busy={busy} onClick={finish} disabled={remaining.length > 0}>
            <PenLine className="size-4" /> Finish
          </Button>
        </div>
      </div>
      {error && (
        <div className="px-4 pt-3 max-w-3xl mx-auto w-full">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
      <div className="flex-1 bg-surface-2 px-2 sm:px-6 py-6">
        {view.docUrl && view.pages && (
          <PdfPages
            url={view.docUrl}
            pages={view.pages}
            overlay={(p, size) => (
              <>
                {(view.others ?? [])
                  .filter((f) => f.page === p)
                  .map((f) => (
                    <div key={f.fid} className="absolute flex items-center overflow-hidden text-black" style={{ ...pos(f), fontSize: Math.max(7, Math.min(12, f.h * size.height * 0.6)) }}>
                      {f.imageUrl ? <img src={f.imageUrl} alt="" className="max-h-full max-w-full object-contain" /> : f.type === "checkbox" ? (f.value === "true" ? "✓" : "") : <span className="truncate">{f.value}</span>}
                    </div>
                  ))}
                {fields
                  .filter((f) => f.page === p)
                  .map((f) => (
                    <MyField
                      key={f.fid}
                      f={f}
                      color={color}
                      fontPx={Math.max(8, Math.min(14, f.h * size.height * 0.62))}
                      widthPx={f.w * size.width}
                      name={view.me.name}
                      today={today}
                      sigUrl={f.type === "signature" ? sig : f.type === "initials" ? ini : null}
                      applied={applied.has(f.fid)}
                      value={values[f.fid]}
                      onApply={() => apply(f)}
                      onValue={(v) => setValues((s) => ({ ...s, [f.fid]: v }))}
                    />
                  ))}
              </>
            )}
          />
        )}
        <div className="max-w-[860px] mx-auto mt-6 flex flex-col sm:flex-row gap-2 sm:items-center">
          <p className="text-xs text-muted">Selecting Finish applies your electronic signature to this document. You'll get the completed copy by email.</p>
          <Button variant="ghost" className="sm:hidden" onClick={() => setDeclining(true)}>
            Decline to sign
          </Button>
        </div>
      </div>
      {pad && (
        <SignaturePad
          kind={pad.kind}
          name={view.me.name}
          saved={view.saved?.[pad.kind]}
          canSave={view.canSave}
          onClose={() => setPad(null)}
          onAdopt={(png, save) => {
            setSaveForNext((s) => ({ ...s, [pad.kind]: save }));
            if (pad.kind === "signature") setSig(png);
            else setIni(png);
            setApplied((s) => new Set(s).add(pad.fid));
            setPad(null);
          }}
        />
      )}
      {declining && <DeclineDialog call={call} onClose={() => setDeclining(false)} />}
    </div>
  );
}

const pos = (f: Field) => ({ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` });

function MyField({ f, color, fontPx, widthPx, name, today, sigUrl, applied, value, onApply, onValue }: { f: Field; color: string; fontPx: number; widthPx: number; name: string; today: string; sigUrl: string | null; applied: boolean; value: string | boolean | undefined; onApply: () => void; onValue: (v: string | boolean) => void }) {
  const box = { ...pos(f), outline: `2px solid ${color}`, outlineOffset: 1, background: `color-mix(in srgb, ${color} 10%, transparent)` };
  if (f.type === "signature" || f.type === "initials") {
    return (
      <div data-fid={f.fid} className="absolute" style={box}>
        <button onClick={onApply} className={cx("w-full h-full flex items-center justify-center overflow-hidden rounded-[2px]", !applied && "animate-pulse")} style={{ color }} aria-label={applied ? `Remove ${FIELD_LABEL[f.type].toLowerCase()}` : `Add your ${FIELD_LABEL[f.type].toLowerCase()}`}>
          {applied && sigUrl ? <img src={sigUrl} alt="" className="max-h-full max-w-full object-contain" /> : <span className="font-semibold whitespace-nowrap" style={{ fontSize: Math.max(7, Math.min(fontPx, 13, widthPx / (f.type === "signature" ? 3 : 4.2))) }}>{f.type === "signature" ? "Sign" : "Initial"}</span>}
        </button>
      </div>
    );
  }
  if (f.type === "checkbox") {
    return (
      <label data-fid={f.fid} className="absolute flex items-center justify-center cursor-pointer" style={box} title={f.label}>
        <input type="checkbox" checked={value === true} onChange={(e) => onValue(e.target.checked)} className="w-[80%] h-[80%] m-0" aria-label={f.label || "Checkbox"} style={{ accentColor: color }} />
      </label>
    );
  }
  if (f.type === "text") {
    return (
      <div data-fid={f.fid} className="absolute" style={box}>
        <input value={String(value ?? "")} onChange={(e) => onValue(e.target.value)} maxLength={500} placeholder={f.label || (f.required ? "Required" : "Optional")} aria-label={f.label || "Text"} className="w-full h-full bg-transparent px-1 text-black placeholder:text-black/40 outline-none" style={{ fontSize: fontPx }} />
      </div>
    );
  }
  // dateSigned / name: filled automatically
  return (
    <div data-fid={f.fid} className="absolute flex items-center px-1 text-black overflow-hidden" style={{ ...box, fontSize: fontPx }} title={f.type === "dateSigned" ? "Filled in when you finish" : "Your name as the sender entered it"}>
      <span className="truncate">{f.type === "dateSigned" ? today : name}</span>
    </div>
  );
}
